import { css, useTheme } from '@emotion/react';
import { AlertCircle } from 'lucide-react';

import { fontSizes, spacing } from '@/theme/tokens';
import { Button } from '@/components/ui/button';

interface AppErrorPageProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export function AppErrorPage({
  title = 'Something went wrong',
  message = 'We ran into a problem loading this page. Please try again.',
  onRetry,
}: AppErrorPageProps) {
  const theme = useTheme();

  return (
    <div
      data-testid="app-error-page"
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 60vh;
        text-align: center;
        gap: ${spacing.md};
        padding: ${spacing.xl};
      `}
    >
      <AlertCircle size={48} color={theme.colors.error} aria-hidden="true" />
      <h1
        css={css`
          font-size: ${fontSizes.xxl};
          font-weight: 700;
          color: ${theme.colors.text};
          margin: 0;
        `}
      >
        {title}
      </h1>
      <p
        css={css`
          color: ${theme.colors.textSecondary};
          margin: 0;
          max-width: 400px;
          line-height: 1.5;
        `}
      >
        {message}
      </p>
      {onRetry && (
        <Button
          onClick={onRetry}
          css={css`
            margin-top: ${spacing.sm};
          `}
        >
          Try Again
        </Button>
      )}
    </div>
  );
}
